//  MUI
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { a2aTransfersSelectors } from 'app-components/flux/a2aTransfers';
import { accountsActions, accountsTypes } from 'app-components/flux/accounts/index';
//  DATA
import type { institutionLoginsTypes } from 'app-components/flux/institution-logins';
import { preferencesActions, preferencesSelectors } from 'app-components/flux/preferences';
import { profileActions, profileSelectors } from 'app-components/flux/profile';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { useFetchOnce } from 'app-components/hooks/utils';
import {
  showAccountLockoutDialog,
  showInitiateTransferDialog,
  showMicrodespositDialog,
} from 'components/Dialogs/A2A/actions';
import type { onboardA2ADialogIndex } from 'components/Dialogs/A2A/onboardA2ADialogIndexEnums';
import { onboardA2ADialogIndexEnums } from 'components/Dialogs/A2A/onboardA2ADialogIndexEnums';
//  CUSTOM COMPONENTS
import {
  DIALOG_TYPE as CONFIRMATION_DIALOG_TYPE,
  mkConfirmationDialogProps,
} from 'components/Dialogs/ConfirmationDialog';
import * as fundingAccountSelectors from 'data/fundingAccounts/selectors';
import type { FundingAccount } from 'data/fundingAccounts/types';
import * as fundingAccountUtils from 'data/fundingAccounts/utils';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import useKickoffA2AFlow from 'hooks/useKickoffA2AFlow';
//  MAIN
import { Map as ImmutableMap } from 'immutable';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { Button, Stack } from '@mui/material';
import QIllustration from './QIllustration';

const useStyles = makeStyles()((theme) => ({
  transferWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  dismissButton: {
    marginRight: 16,
  },
  buttonWrapper: {
    display: 'flex',
    flexDirection: 'row',
  },
  wrapper: {
    width: '100%',
    // height: 78,
    backgroundColor: theme.palette.info.light,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    '&.error': {
      borderColor: theme.palette.warning.main,
      backgroundColor: theme.palette.background.paper,
    },
  },
  leftText: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
}));
type A2AEntryButtonProps = {
  institutionLogin: institutionLoginsTypes.InstitutionLogin;
  account: accountsTypes.Account;
  hideOnVerified?: boolean;
  onlyShowVerified?: boolean;
  disableDefaultStyling?: boolean;
};
function A2AEntryButton(props: A2AEntryButtonProps) {
  const { classes, cx } = useStyles();
  const dispatch = useAppDispatch();
  useFetchOnce(profileActions.getProfile, profileSelectors);
  const a2a = useAppSelector(
    (state) => preferencesSelectors.getSharedPreferencesByPath(state, { group: 'dataset' }).a2a,
  );
  const { institutionLogin, account, hideOnVerified, onlyShowVerified, disableDefaultStyling } = props;
  const shouldHideA2ASetupBanner = (account && account.isA2ABannerDismissed) || (a2a && a2a.hideAllTransactionBanners);
  const kickoffA2AFlow = useKickoffA2AFlow();
  const isMicrodepositEligible = useAppSelector((state) =>
    fundingAccountSelectors.isAccountEligibleForMicrodeposits(state, account),
  );

  const isMoveMoneyEligible = useAppSelector((state) =>
    fundingAccountSelectors.isAccountEligibleForCashTransfer(state, account),
  );

  const { isEligible } = useAppSelector((state) =>
    fundingAccountSelectors.isAccountEligibleForTransfers(state, account),
  );

  const isDisabled = useAppSelector((state) => fundingAccountSelectors.isAccountDisabled(state, account)).status;

  const fundingAccountsByAccountId = useAppSelector(fundingAccountSelectors.getAllFundingAccountsByAccountId);
  const a2aTransferEnabled = useAppSelector(a2aTransfersSelectors.a2aTransferEnabled);
  const myFundingAccount: FundingAccount | undefined = fundingAccountsByAccountId.get(account?.id || '');
  const depositOnly = fundingAccountUtils.isAccountA2aDepositOnly(account); // Show unique text for deposit only
  const accountStatus = {
    eligible: {
      enableTransferFunctions: a2aTransferEnabled && isEligible && !onlyShowVerified,
    },
    depositPending: {
      enableTransferFunctions: a2aTransferEnabled && isMicrodepositEligible?.isEligible && !onlyShowVerified,
    },
    verified: {
      enableTransferFunctions: a2aTransferEnabled && isMoveMoneyEligible?.isEligible && !hideOnVerified,
    },
    fundingAccountFailure: {
      showChip: isDisabled && !onlyShowVerified,
    },
  };

  const dismissButtonClick = () => {
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'close-account',
          type: CONFIRMATION_DIALOG_TYPE,
          allowNesting: true,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              title: 'Dismiss Transfers',
              content:
                'You won’t see this again for this account. To set up transfers for this or other accounts in the future, go to Settings > Accounts.',
              confirmLabel: 'Dismiss',
              denyLabel: 'Cancel',
              size: 'xs',
              tertiaryLabelVariant: 'text',
              tertiaryLabel: 'Dismiss for all accounts',
              tertiaryAction: () => {
                dispatch(
                  preferencesActions.setPreference({
                    section: 'shared',
                    group: 'dataset',
                    preference: { a2a: { hideAllTransactionBanners: true } },
                  }),
                );
              },
              confirmAction: () =>
                dispatch(
                  accountsActions.updateAccount(
                    accountsTypes.mkUpdateAccount(account?.type, { id: account?.id, isA2ABannerDismissed: true }),
                    {
                      undo: { userMessage: 'Banner dismissed' },
                    },
                  ),
                ),
            }),
          ),
        }),
      ),
    );
  };
  const buttonClick = () => {
    if (accountStatus.eligible.enableTransferFunctions) {
      kickoffA2AFlow({
        initiator: 'transactions',
        startTabIndex: onboardA2ADialogIndexEnums.ACCT_INFO as onboardA2ADialogIndex,
        initialInstitutionId: institutionLogin?.institutionId || '',
        initialAccountId: account?.id || '',
      });
    } else if (accountStatus.depositPending.enableTransferFunctions) {
      dispatch(
        showMicrodespositDialog({
          fundingAccountId: myFundingAccount?.id,
          hideAccountPicker: true,
          initiator: 'transactions',
        }),
      );
    } else if (accountStatus.verified.enableTransferFunctions) {
      dispatch(
        showInitiateTransferDialog({
          initiator: 'transactions',
          transferOptions: {
            fromAccount: account,
          },
        }),
      );
    } else if (accountStatus.fundingAccountFailure.showChip) {
      dispatch(
        showAccountLockoutDialog({
          fundingAccountId: myFundingAccount?.id,
        }),
      );
    }
  };
  return (
    a2aTransferEnabled &&
    account && (
      <>
        {(accountStatus.eligible.enableTransferFunctions || accountStatus.depositPending.enableTransferFunctions) &&
          !shouldHideA2ASetupBanner && (
            <Paper className={disableDefaultStyling ? '' : classes.wrapper}>
              <Stack gap={2} direction='row' alignItems='center'>
                <QIllustration name='transferCards' size='xs' />
                {/* <CompareArrowsRoundedIcon className={classes.titleIcon} /> */}
                <div className={classes.leftText}>
                  <Typography variant='paragraphMedium' color='text.primary'>
                    {accountStatus.eligible.enableTransferFunctions && 'Move money directly in Simplifi'}
                    {accountStatus.depositPending.enableTransferFunctions && 'Verify this account for transfers'}
                  </Typography>
                  <Typography variant='paragraphRegular' color='text.secondary'>
                    {accountStatus.eligible.enableTransferFunctions &&
                      depositOnly &&
                      'Set up this account now to start receiving funds!'}
                    {accountStatus.eligible.enableTransferFunctions &&
                      !depositOnly &&
                      'Start transferring funds with this account now.'}
                    {accountStatus.depositPending.enableTransferFunctions &&
                      'Confirm the two small deposit amounts we recently sent to this account.'}
                  </Typography>
                </div>
              </Stack>
              <div className={classes.buttonWrapper}>
                <Button
                  size='small'
                  className={classes.dismissButton}
                  id='a2a-dismiss-button'
                  onClick={dismissButtonClick}
                  variant='text'
                >
                  Dismiss
                </Button>
                <Button
                  size='small'
                  id='a2a-begin-button'
                  onClick={buttonClick}
                  disabled={
                    !accountStatus.eligible.enableTransferFunctions &&
                    !accountStatus.depositPending.enableTransferFunctions &&
                    !accountStatus.verified.enableTransferFunctions
                  }
                  variant={accountStatus.depositPending.enableTransferFunctions ? 'contained' : 'outlined'}
                >
                  {accountStatus.eligible.enableTransferFunctions && 'Set up transfers'}
                  {accountStatus.depositPending.enableTransferFunctions && 'Verify'}
                </Button>
              </div>
            </Paper>
          )}
        {accountStatus.fundingAccountFailure.showChip && (
          <Paper className={cx(disableDefaultStyling ? '' : classes.wrapper, 'error')}>
            <div className={classes.leftText}>
              <Typography variant='paragraphMedium' color='text.primary'>
                Account verification failed
              </Typography>
              <Typography variant='paragraphRegular' color='text.secondary'>
                We had trouble delivering verification deposits to this account.
              </Typography>
            </div>
            <Button size='small' id='a2a-begin-button' onClick={buttonClick} variant='contained'>
              View
            </Button>
          </Paper>
        )}
        {accountStatus.verified.enableTransferFunctions && (
          <div className={disableDefaultStyling ? '' : classes.transferWrapper}>
            <Button
              size='small'
              id='a2a-begin-button'
              onClick={buttonClick}
              startIcon={<CompareArrowsRoundedIcon />}
              variant='outlined'
            >
              Transfer Money
            </Button>
          </div>
        )}
      </>
    )
  );
}
export default A2AEntryButton;
